/**
 * Resolución de origen para herencia de Zocalo 4000 / tirador.
 */

import { isIndependentZocaloLinkedSon } from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import {
  fetchZoneAcabadoInfo,
  resolveTextureCatalogMatch,
} from '@utils/sceneObjects/linked/resolveTextureCatalogMatch'
import {
  fetchCatalogTexturesForAcabado,
  findMatchingCatalogTexture,
} from '@utils/sceneObjects/linked/textureCatalogApi'

export function normalizeInheritSourceId(id?: unknown): string | null {
  const value = String(id ?? '').trim()
  return value || null
}

function isIndependentZocaloFront(item: any): boolean {
  return (
    Boolean(item) &&
    isIndependentZocaloLinkedSon(item) &&
    !(item as { isIndependentZocaloReturn?: boolean }).isIndependentZocaloReturn
  )
}

export function findIndependentZocaloOnParent(
  items: Map<string, any>,
  parent: any,
): any | null {
  if (!parent) return null
  const srcZocaloId = parent.independentZocaloSonId
  if (srcZocaloId != null) {
    const son = items.get(srcZocaloId as string)
    if (isIndependentZocaloFront(son) && Array.isArray(son.textures) && son.textures.length) {
      return son
    }
  }
  if (parent.childs?.size) {
    for (const childId of parent.childs) {
      const child = items.get(childId as string)
      if (isIndependentZocaloFront(child) && Array.isArray(child?.textures) && child.textures.length) {
        return child
      }
    }
  }
  for (const candidate of items.values()) {
    if (candidate?.type !== 'linkedSon') continue
    if (candidate.firstParentID !== parent.id) continue
    if (isIndependentZocaloFront(candidate) && Array.isArray(candidate.textures) && candidate.textures.length) {
      return candidate
    }
  }
  return null
}

function mergePuertaWithZone(carrier: any, tex: any) {
  if (!tex) return null
  const zones = carrier?.zones
  const zoneKey =
    (tex.zoneName && zones?.[tex.zoneName] ? tex.zoneName : null) ||
    Object.keys(zones ?? {}).find((key) =>
      /puerta/i.test(
        String(zones[key]?.material?.zoneDisplayName ?? zones[key]?.material?.zone ?? ''),
      ),
    )
  const mat = zoneKey ? zones?.[zoneKey]?.material : null
  if (!mat) {
    return {
      ...tex,
      zoneDisplayName: tex.zoneDisplayName || 'Puerta',
    }
  }
  return {
    ...tex,
    ...mat,
    ...tex,
    complemento: tex.complemento || mat.complemento,
    description: tex.description || mat.description || mat.descripcion,
    descripcion: tex.descripcion || mat.descripcion || mat.description,
    name: tex.name || mat.name,
    linkedTextureID: tex.linkedTextureID || mat.linkedTextureID,
    searchID: tex.searchID || mat.searchID,
    id: tex.id || mat.id,
    url: tex.url || mat.url,
    acabado: tex.acabado || mat.acabado,
    zoneDisplayName: tex.zoneDisplayName || mat.zoneDisplayName || 'Puerta',
    zone: tex.zone || mat.zone || 'Puerta',
  }
}

function findPuertaFromZones(parent: any) {
  if (!parent?.zones) return null
  for (const zone of Object.values(parent.zones) as any[]) {
    const mat = zone?.material
    if (!mat) continue
    if (!/puerta/i.test(String(mat.zoneDisplayName ?? mat.zone ?? zone?.name ?? ''))) continue
    return {
      ...mat,
      zoneDisplayName: mat.zoneDisplayName || 'Puerta',
      zone: mat.zone || 'Puerta',
      zoneName: zone?.name || mat.zoneName,
    }
  }
  return null
}

export function findPuertaTextureOnItem(items: Map<string, any>, parent: any): any | null {
  if (!parent) return null
  const onParent = findTextureByZoneHint(parent.textures, /puerta/i)
  if (onParent) return mergePuertaWithZone(parent, onParent)
  const fromZones = findPuertaFromZones(parent)
  if (fromZones) return fromZones
  if (parent.childs?.size) {
    for (const childId of parent.childs) {
      const child = items.get(String(childId))
      if (!child) continue
      const fromChild = findTextureByZoneHint(child.textures, /puerta/i)
      if (fromChild) return mergePuertaWithZone(child, fromChild)
      if (/puerta/i.test(String(child.description ?? child.typeOfSon ?? ''))) {
        return mergePuertaWithZone(child, child.textures?.[0] ?? findPuertaFromZones(child))
      }
    }
  }
  for (const candidate of items.values()) {
    if (candidate?.type !== 'linkedSon') continue
    if (candidate.firstParentID !== parent.id) continue
    const fromChild = findTextureByZoneHint(candidate.textures, /puerta/i)
    if (fromChild) return mergePuertaWithZone(candidate, fromChild)
  }
  return null
}

export function looksLikeZocaloCatalogName(value: string): boolean {
  const v = String(value ?? '').trim()
  if (!v || /\s/.test(v)) return false
  return /^z[a-z0-9._-]+$/i.test(v)
}

export function getPuertaComplementoForZocalo(puertaTex: any): string {
  const complemento = String(puertaTex?.complemento ?? '').trim()
  if (complemento) return complemento
  const name = String(puertaTex?.name ?? '').trim()
  if (looksLikeZocaloCatalogName(name)) return name
  return ''
}

function catalogComplemento(entry: { complementos?: string; complemento?: string } | undefined) {
  return String(entry?.complementos ?? entry?.complemento ?? '').trim()
}

export async function lookupPuertaComplementoFromCatalog(
  puertaTex: any,
  parent?: any,
): Promise<string> {
  if (!puertaTex) return ''
  try {
    const texture = {
      ...puertaTex,
      zoneDisplayName: puertaTex.zoneDisplayName || 'Puerta',
      zone: puertaTex.zone || 'Puerta',
    }
    const match = await resolveTextureCatalogMatch(texture, {
      idUnidades: Number(parent?.idUnidades) || undefined,
      catalogByAcabadoId: new Map(),
    })
    const fromMatch = catalogComplemento(match?.catalogTexture)
    if (fromMatch) return fromMatch

    const linkedId = Number(puertaTex.linkedTextureID)
    if (!linkedId) return ''
    const zoneList = await fetchZoneAcabadoInfo(linkedId)
    const puertaZone =
      zoneList.find((zone) => /puerta/i.test(String(zone.zona ?? ''))) ?? zoneList[0]
    const idAcabado =
      puertaZone?.acabados?.[0]?.idAcabadoUnidadModeloAcabado ||
      puertaZone?.acabados?.[0]?.idAcabadoUnidad
    if (!idAcabado) return ''
    const catalog = await fetchCatalogTexturesForAcabado(idAcabado)
    const exact = findMatchingCatalogTexture(texture, catalog)
    if (catalogComplemento(exact)) return catalogComplemento(exact)

    const doorLabel = String(
      puertaTex.name || puertaTex.description || puertaTex.descripcion || '',
    )
      .trim()
      .toLowerCase()
    const doorCode = doorLabel.split(/\s+/)[0]
    const loose = catalog.find((entry) => {
      const nombre = String(entry.nombre ?? '').toLowerCase()
      const descripcion = String(entry.descripcion ?? '').toLowerCase()
      return (
        (doorLabel && (nombre === doorLabel || descripcion === doorLabel)) ||
        (doorCode && (nombre === doorCode || nombre.startsWith(`${doorCode} `)))
      )
    })
    return catalogComplemento(loose)
  } catch {
    return ''
  }
}

export async function resolveZocaloComplementoFromPuerta(
  puertaTex: any,
  parent?: any,
): Promise<{ complementoName: string; resolvedFrom: 'zone' | 'catalog' | '' }> {
  const fromField = getPuertaComplementoForZocalo(puertaTex)
  if (fromField && (looksLikeZocaloCatalogName(fromField) || !/\s/.test(fromField))) {
    return { complementoName: fromField, resolvedFrom: 'zone' }
  }
  const fromCatalog = await lookupPuertaComplementoFromCatalog(puertaTex, parent)
  if (fromCatalog) {
    return { complementoName: fromCatalog, resolvedFrom: 'catalog' }
  }
  return { complementoName: fromField, resolvedFrom: fromField ? 'zone' : '' }
}

export function resolvePuertaForZocalo(
  items: Map<string, any>,
  preferredId: string | null,
  fallbackItem?: any,
): {
  parent: any
  puertaTex: any
  complementoName: string
  from: 'selected' | 'drawnUnit' | 'scene'
} | null {
  const tryParent = (
    parent: any,
    from: 'selected' | 'drawnUnit' | 'scene',
  ) => {
    if (!parent) return null
    const puertaTex = findPuertaTextureOnItem(items, parent)
    if (!puertaTex) return null
    const complementoName = getPuertaComplementoForZocalo(puertaTex)
    return { parent, puertaTex, complementoName, from }
  }

  if (preferredId) {
    const hit = tryParent(items.get(preferredId), 'selected')
    if (hit) return hit
  }

  const own = tryParent(fallbackItem, 'drawnUnit')
  if (own) return own

  for (const candidate of items.values()) {
    if (!candidate || candidate.type !== 'linkedItem') continue
    if (fallbackItem && candidate.id === fallbackItem.id) continue
    const hit = tryParent(candidate, 'scene')
    if (hit) return hit
  }

  return null
}

export function resolveZocaloInheritSource(
  items: Map<string, any>,
  preferredId: string | null,
  excludeId?: string | null,
): { sourceItem: any; sourceZocalo: any; from: 'selected' | 'scene' } | null {
  const preferred = preferredId ? items.get(preferredId) : null
  if (preferred && preferred.id !== excludeId) {
    const sourceZocalo = findIndependentZocaloOnParent(items, preferred)
    if (sourceZocalo) {
      return { sourceItem: preferred, sourceZocalo, from: 'selected' }
    }
  }

  for (const candidate of items.values()) {
    if (!candidate || candidate.id === excludeId) continue
    if (candidate.type !== 'linkedItem') continue
    const sourceZocalo = findIndependentZocaloOnParent(items, candidate)
    if (sourceZocalo) {
      return { sourceItem: candidate, sourceZocalo, from: 'scene' }
    }
  }

  return null
}

export function findTextureByZoneHint(textures: any[] | undefined | null, hint: RegExp) {
  if (!Array.isArray(textures)) return null
  return (
    textures.find((texture) =>
      hint.test(String(texture?.zoneDisplayName ?? texture?.zoneName ?? texture?.zone ?? '')),
    ) ?? null
  )
}
